const UuidFilePattern =
	/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}\.csv$/i;

export type AnalysisCsvStoragePath = {
	objectId: string;
	storagePath: string;
};

/**
 * Storage prefix for an analysis CSV. Matches the legacy object
 * `{userId}/analyses/{analysisId}` and nested `{uuid}.csv` uploads under it.
 */
export function getAnalysisCsvStoragePrefix(
	userId: string,
	analysisId: string,
): string {
	return `${userId}/analyses/${analysisId}`;
}

/**
 * Builds the Storage object path for an analysis CSV.
 * The object name is a UUID so two uploads named "invoices.csv" cannot collide.
 */
export function createAnalysisCsvStoragePath(
	userId: string,
	analysisId: string,
	objectId = crypto.randomUUID(),
): AnalysisCsvStoragePath {
	return {
		objectId,
		storagePath: `${getAnalysisCsvStoragePrefix(userId, analysisId)}/${objectId}.csv`,
	};
}

export function isAnalysisCsvStoragePath(
	storagePath: string,
	options: { ownerId: string; analysisId: string },
): boolean {
	const expectedPrefix = `${getAnalysisCsvStoragePrefix(options.ownerId, options.analysisId)}/`;
	if (
		!storagePath ||
		storagePath.includes('..') ||
		storagePath.includes('\\') ||
		!storagePath.startsWith(expectedPrefix)
	) {
		return false;
	}

	const objectName = storagePath.slice(expectedPrefix.length);
	return UuidFilePattern.test(objectName) && !objectName.includes('/');
}

/**
 * Whether this analysis should calculate from the retained CSV.
 * Missing/empty means the legacy Firestore invoices subcollection path.
 * CSV-sourced calcs depend on `createInvoiceId()` matching import.
 */
export function hasAnalysisCsvStoragePath(
	storagePath: string | undefined | null,
): storagePath is string {
	return typeof storagePath === 'string' && storagePath.length > 0;
}
